import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';
import type { VisitType } from '../api/types';
import { tn } from '../i18n';
import { useTranslate } from '../i18n/react';
import { CountChip, DefinitionTable } from './DefinitionTable';

interface Draft {
  name: string;
  description: string;
}

/**
 * Visit types, through the shared definition table.
 *
 * <p>This screen used to carry its own copy of the list, the in-place editor and the delete
 * button, written before the shared one existed — about a hundred and seventy lines that
 * said the same thing slightly differently. Deleting them is the point of having the shared
 * table at all: the next change to how a definition screen behaves happens once.</p>
 */
export function VisitTypes() {
  const t = useTranslate();
  const [rows, setRows] = useState<VisitType[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await api.visitTypes.list());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  return (
    <DefinitionTable<VisitType, Draft>
      crumbs={[t('app.definitions'), t('nav.visitTypes')]}
      title={t('visitTypes.title')}
      subtitle={t('visitTypes.subtitle')}
      rows={rows}
      loading={loading}
      blank={{ name: '', description: '' }}
      toDraft={(row) => ({ name: row.name, description: row.description ?? '' })}
      fields={[
        { key: 'name', label: t('visitTypes.name'), placeholder: t('visitTypes.namePlaceholder') },
        { key: 'description', label: t('visitTypes.description') },
      ]}
      columns={[
        {
          label: t('visitTypes.templates'),
          render: (row) => <CountChip count={row.usedInTemplates} label={t('visitTypes.templateChip')} />,
        },
      ]}
      cell={(row, key) =>
        key === 'description'
          ? (row.description ?? <span className="nv-subtle">{t('common.none')}</span>)
          : row.name
      }
      searchable={(row) => `${row.name} ${row.description ?? ''}`}
      searchPlaceholder={t('visitTypes.searchPlaceholder')}
      describe={(row) => ({
        name: row.name,
        warning: row.usedInTemplates > 0 ? tn('common.inUse', row.usedInTemplates) : undefined,
      })}
      empty={t('visitTypes.empty')}
      emptyText={t('visitTypes.emptyText')}
      save={async (draft, id) => {
        const body = { name: draft.name.trim(), description: draft.description.trim() || null };
        if (id) await api.visitTypes.update(id, body);
        else await api.visitTypes.create(body);
        await reload();
      }}
      remove={async (row) => {
        await api.visitTypes.remove(row.id);
        await reload();
      }}
    />
  );
}
